import { DRAFT } from '@constants/documents.constants';
import { FeatureFlags, Professional, SendingsDashboardType } from '@domain/types';
import { getProfessionalAsReference } from '@domain/utils';
import {
  FeatureFlagsContext,
  FeatureFlagsLoadingContext,
} from '@launcher/context/FeatureFlags.context';
import useDocumentIdentities from '@lifen-sending/hooks/useDocumentIdentities';
import { LaunchDarklyService } from '@services/featureFlags/launchDarklyService';
import { legacyFeatureFlagsStore } from '@services/featureFlags/legacyFeatureFlags.store';
import { logger } from '@services/logger/logger.service';
import { DesktopActions } from '@store/desktop/desktop.action';
import { selectDashboardSearchParams } from '@store/documents/documents.selector';
import { refreshDraftCounters } from '@store/documents/outgoing/draft.slice';
import { State } from '@store/reducer';
import { initFeatureFlags } from '@store/user/user.actions';
import {
  getApiDomain,
  getIdentitiesReferences,
  getSelectedIdentity,
  getUser,
} from '@store/user/user.selector';
import { useQueryClient } from '@tanstack/react-query';
import { prefetchSendingRequests } from '@utils/caching.util';
import { LDContext } from 'launchdarkly-js-client-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const FeatureFlagsProvider = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const workspaceId = useSelector(getUser).currentWorkspaceId;
  const [isFetchingIdentitiesEnabled, setIsFetchingIdentitiesEnabled] = useState(false);
  const [flags, setFlags] = useState<FeatureFlags>({});
  const [isLoading, setIsLoading] = useState(true);
  useDocumentIdentities(isFetchingIdentitiesEnabled);
  const userIdentitiesReferences = useSelector(getIdentitiesReferences);
  const apiDomain = useSelector((state: State) => getApiDomain(state));
  const selectedSendingIdentity = useSelector((state: State) =>
    getSelectedIdentity(state, 'lifenSending'),
  );

  const searchParamsDraft = useSelector((state: State) =>
    selectDashboardSearchParams(state, DRAFT),
  );
  const queryClient = useQueryClient();

  const initializedRef = useRef(false);

  const client = useRef<LaunchDarklyService>(undefined);

  const handleWatchersPollingChange = useCallback(
    (enabled: boolean | undefined) => {
      if (enabled !== undefined) {
        // TODO : Move me into a saga
        dispatch(DesktopActions.startWatchers());
      } // or DesktopActions.initDesktop?
    },
    [dispatch],
  );

  const handleMaintenanceChange = useCallback(
    (isMaintenance: boolean | undefined) => {
      if (isMaintenance === true) {
        dispatch(DesktopActions.closeWatchers()); // does not work in initDesktop.sagas
      } else if (isMaintenance === false) {
        dispatch(DesktopActions.initDesktop());
      }
    },
    [dispatch],
  );

  const handleSentryTracingChange = useCallback((isTracing: boolean | undefined) => {
    if (isTracing === true) {
      localStorage.setItem('cafe-sentry-tracing', '1');
    } else if (isTracing === false) {
      localStorage.setItem('cafe-sentry-tracing', '0');
    }
  }, []);

  const handleFlagsReady = useCallback(
    (initializedFlags: FeatureFlags) => {
      // Before the dispatch: the sagas it starts read the flags from the store.
      legacyFeatureFlagsStore.set(initializedFlags);
      setFlags(initializedFlags);
      setIsLoading(false);
      dispatch(initFeatureFlags(initializedFlags));

      if (user.settings.sendingShortcutEnabled && !initializedFlags['cafe-maintenance']) {
        // Prefetch lifen sending data if activated. Not under the v2 dashboard flag: nothing reads
        // this v1 cache once the v2 dashboards render, and warming it anyway is what kept the v1
        // dashboard cache non-empty under the flag (see CONTEXT.md, "Sending request cache").
        if (!initializedFlags['cafe-doc-use-v2-sending-endpoint']) {
          // Only prefetch the general Draft dashboard (all identities)
          // Individual identity dashboards will load on-demand via useSendingRequests
          prefetchSendingRequests({
            dashboardType: SendingsDashboardType.Draft,
            currentIdentity: undefined, // General dashboard
            searchParams: searchParamsDraft,
            dispatchFn: dispatch,
            queryClient,
            featureFlags: initializedFlags,
          });

          // Note: Sent, Inbox, Archive dashboards will load on-demand when navigated to
        }

        // Fetch lightweight counters (no document data) — the same sidebar under either
        // dashboard, so unlike the v1 prefetch above this is not gated by the v2 flag.
        dispatch(refreshDraftCounters());
      }

      // Handle Sentry tracing
      localStorage.setItem(
        'cafe-sentry-tracing',
        initializedFlags['cafe-sentry-tracing'] ? '1' : '0',
      );
    },
    [dispatch, user.settings.sendingShortcutEnabled, searchParamsDraft, queryClient],
  );

  const handleFlagsChange = useCallback(
    (changedFlags: Partial<FeatureFlags>) => {
      legacyFeatureFlagsStore.merge(changedFlags);
      setFlags((previous) => ({ ...previous, ...changedFlags }));
      setIsLoading(false);

      if ('enable-watchers-polling' in changedFlags) {
        handleWatchersPollingChange(changedFlags['enable-watchers-polling']);
      }
      if ('cafe-maintenance' in changedFlags) {
        handleMaintenanceChange(changedFlags['cafe-maintenance']);
      }
      if ('cafe-sentry-tracing' in changedFlags) {
        handleSentryTracingChange(changedFlags['cafe-sentry-tracing']);
      }
    },
    [dispatch, handleWatchersPollingChange, handleMaintenanceChange, handleSentryTracingChange],
  );

  const buildLDContext = useCallback(
    (identity?: Professional): LDContext => {
      const userCtx = {
        key: user.uuid,
        workspaceId,
        identities: userIdentitiesReferences,
        apiDomain,
      };
      if (!identity) return { kind: 'user', ...userCtx };
      return {
        kind: 'multi',
        user: userCtx,
        identity: { key: getProfessionalAsReference(identity) },
      };
    },
    [user.uuid, workspaceId, userIdentitiesReferences, apiDomain],
  );

  useEffect(() => {
    if (!user.uuid || !workspaceId || !userIdentitiesReferences.length) return;
    const ctx = buildLDContext(selectedSendingIdentity);
    if (!initializedRef.current) {
      logger.info('LOGIC', `LaunchDarkly userId: ${user.uuid}`);
      setIsFetchingIdentitiesEnabled(true);
      client.current ??= new LaunchDarklyService();
      client.current.onFlagsReady = handleFlagsReady;
      client.current.onFlagsChange = handleFlagsChange;
      client.current.initialize(ctx);
      initializedRef.current = true;
      return;
    }
    client.current?.identify(ctx).catch((error: unknown) => {
      logger.error('LOGIC', `LaunchDarkly identify failed: ${error}`);
    });
  }, [
    user.uuid,
    workspaceId,
    userIdentitiesReferences,
    selectedSendingIdentity,
    handleFlagsReady,
    handleFlagsChange,
    buildLDContext,
  ]);

  return (
    <FeatureFlagsLoadingContext.Provider value={isLoading}>
      <FeatureFlagsContext.Provider value={flags}>{children}</FeatureFlagsContext.Provider>
    </FeatureFlagsLoadingContext.Provider>
  );
};
